import { formatDate } from './format-date';

export const DATE_MATH = 'dateMath';

interface GetDateValueProps {
  value: string | number;
}

export function getDateValueString({ value }: GetDateValueProps): string {
  const { baseDate, years, months, days, hours, minutes, seconds } = getDateValues({ value });
  const strBaseDate = typeof baseDate === 'number' ? formatDate(baseDate) : baseDate;

  /**
   * Support dateMath format. V2
   */
  if (typeof value === 'string' && value.includes(DATE_MATH)) {
    const strYears = formatOffset({ amount: years, singular: 'año', plural: 'años' });
    const strMonths = formatOffset({ amount: months, singular: 'mes', plural: 'meses' });
    const strDays = formatOffset({ amount: days, singular: 'día', plural: 'días' });
    const strHours = formatOffset({ amount: hours, singular: 'hora', plural: 'horas' });
    const strMinutes = formatOffset({ amount: minutes, singular: 'minuto', plural: 'minutos' });
    const strSeconds = formatOffset({ amount: seconds, singular: 'segundo', plural: 'segundos' });

    return `[${strBaseDate}] ${strYears} ${strMonths} ${strDays} ${strHours} ${strMinutes} ${strSeconds}`.trim();
  }

  /**
   * Support timestamp format. V1
   */
  return strBaseDate;
}

interface ParseDateMathStringReturn {
  baseDate: number | string;
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

function parseDateMathString({ value }: { value: string }): ParseDateMathStringReturn {
  const result: ParseDateMathStringReturn = { baseDate: '' };

  const start = value.indexOf('{');
  const end = value.lastIndexOf('}');

  if (start === -1 || end === -1 || end <= start) return result;

  const inner = value.slice(start + 1, end); // baseDate: '', days: -20...
  const params = inner
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  for (const param of params) {
    const [rawKey, ...rest] = param.split(':');

    if (!rawKey || rest.length === 0) continue;

    const key = rawKey.trim();
    const rawValue = rest.join(':').trim(); // in case there was ":" in the value

    if (key === 'baseDate') {
      if (
        (rawValue.startsWith("'") && rawValue.endsWith("'")) ||
        (rawValue.startsWith('"') && rawValue.endsWith('"'))
      ) {
        const unquoted = rawValue.slice(1, -1);
        result.baseDate = unquoted;
      } else {
        const num = Number(rawValue);
        result.baseDate = Number.isNaN(num) ? rawValue : num;
      }
    } else if (
      key === 'years' ||
      key === 'months' ||
      key === 'days' ||
      key === 'hours' ||
      key === 'minutes' ||
      key === 'seconds'
    ) {
      const num = Number(rawValue);
      if (!Number.isNaN(num)) {
        (result as any)[key] = num;
      }
    }
  }

  return result;
}

export function getDateValues({ value }: GetDateValueProps): ParseDateMathStringReturn {
  /**
   * Support dateMath format. V2
   */
  if (typeof value === 'string' && value.includes(DATE_MATH)) {
    return parseDateMathString({ value });
  }

  /**
   * Support timestamp format. V1
   */
  return { baseDate: value };
}

interface FormatOffsetProps {
  amount: number | undefined;
  singular: string;
  plural: string;
}

function formatOffset({ amount, singular, plural }: FormatOffsetProps): string {
  if (amount == null || amount === 0) return '';

  if (amount > 0) {
    const label = amount === 1 ? singular : plural;
    return `+${amount} ${label}`;
  }

  const label = Math.abs(amount) === 1 ? singular : plural;
  return `${amount} ${label}`;
}
